import styled from "@emotion/styled";
import { Button, TextField, Typography } from "@mindlercare/ui-web";
import { normalise } from "personnummer.js";
import { FormEvent, useState } from "react";
import { useInternalAuth } from "../AuthenticationProvider";
import { Spinner } from "../components/Spinner";
import { useTranslation } from "../hooks/useTranslation";

const SOCIAL_SECURITY_NUMBER_LENGTH = 10;

export const MagicLogin = () => {
  const { t } = useTranslation();
  const { canUseMagicLogin, magicLogin, market } = useInternalAuth();
  const [value, onInputChange] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  if (!canUseMagicLogin()) return null;

  const handleMagicLogin = async (event: FormEvent) => {
    event.preventDefault();
    setIsPending(true);
    try {
      const isEmail = value.includes("@");
      const isSSN = value.length >= SOCIAL_SECURITY_NUMBER_LENGTH;
      const normalized = isSSN && !isEmail ? normalise(value) : value;
      const response = await magicLogin(normalized);
      if (!response) {
        setErrorMessage(
          `Failed to use Magic Login - please verify that ${
            isEmail ? "Email" : isSSN ? "SSN" : "UserID"
          } ${normalized} exists in this environment and that you are on VPN`,
        );
        setIsPending(false);
      }
    } catch (e) {
      console.error("Error With Magic Login", {
        error: e,
        errorMessage: e instanceof Error ? e.message : JSON.stringify(e),
      });
      onInputChange("");
      setIsPending(false);
    }
  };

  const placeholder =
    market === "SE"
      ? "Enter userId or national identity number"
      : "Enter userId or email";

  return (
    <form onSubmit={handleMagicLogin}>
      <Container>
        <Typography variant="body-small">
          {"☣️ To use 🪄 Magic Login 🦄 you need to be on Office VPN ☣️"}
        </Typography>

        <TextField
          placeholder={placeholder}
          value={value}
          onChange={(e) => onInputChange(e.target.value)}
          hideSensitiveData={true}
          error={errorMessage !== undefined}
          errorMessage={errorMessage}
          width="full"
        />
        <Button type="submit" variant="primary" disabled={isPending || !value}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isPending && <Spinner size="sm" />}
            {t("LOGIN")}
          </div>
        </Button>
      </Container>
    </form>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 375px;
  align-items: flex-start;
`;
